import { Upload } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText } from './product-fields'
import type { Translate } from './product-types'

/**
 * Props for {@link ProductImageView}. The pre-check and the upload sequence live
 * in the page hook; this view reports the chosen file and renders the result.
 *
 * @property imageUrl - The preview's download URL, or null for the placeholder.
 * @property hasImage - True when the product references an image (Remove is offered).
 * @property canEdit - True with `product.write` and `file.write`.
 * @property isBusy - An upload or remove is in flight: the picker and Remove are withheld until it settles.
 */
export interface ProductImageViewProps {
	t: Translate
	imageUrl: string | null
	hasImage: boolean
	canEdit: boolean
	isBusy: boolean
	error: string | null
	onFileSelected: (file: File) => void
	onRemove: () => void
}

/** The product image: preview or placeholder, upload and remove (organism). */
export function ProductImageView(props: ProductImageViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('image.title')}</h2>
			{props.imageUrl ? (
				<img src={props.imageUrl} alt={t('image.preview')} className="h-32 w-32 rounded-ds-md object-cover" />
			) : (
				<span role="img" aria-label={t('list.noImage')} className="block h-32 w-32 rounded-ds-md bg-ds-surface-muted" />
			)}
			{props.canEdit && props.isBusy ? <p role="status">{t('image.uploading')}</p> : null}
			{props.canEdit && !props.isBusy ? (
				<>
					<Upload
						label={t('image.choose')}
						hint={t('image.hint')}
						accept="image/png,image/jpeg,image/webp"
						files={[]}
						onFilesSelected={(files) => (files[0] ? props.onFileSelected(files[0]) : undefined)}
					/>
					{props.hasImage ? (
						<Button variant="muted" onPress={props.onRemove}>
							{t('image.remove')}
						</Button>
					) : null}
				</>
			) : null}
			<ErrorText message={props.error} />
		</section>
	)
}
